import { useEffect, useRef, type ReactNode } from 'react'

import { cn } from './cn'

export interface ModalProps {
  open: boolean
  onClose: () => void
  title?: ReactNode
  description?: ReactNode
  /** "sm" | "md" | "lg" | "xl" — largeur max. Défaut "md". */
  size?: 'sm' | 'md' | 'lg' | 'xl'
  /** Désactive la fermeture en cliquant sur le backdrop. */
  disableBackdropClose?: boolean
  children?: ReactNode
  footer?: ReactNode
}

const SIZES: Record<NonNullable<ModalProps['size']>, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-2xl',
}

/**
 * Modal overlay simple, centré, avec close-on-Escape et close-on-backdrop.
 * Pas de focus trap complet — adapté à un usage formulaire court.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  size = 'md',
  disableBackdropClose,
  children,
  footer,
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current()
    }
    window.addEventListener('keydown', onKey)
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    dialogRef.current?.focus()
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = previousOverflow
    }
  }, [open])

  if (!open) return null

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={(e) => {
        if (!disableBackdropClose && e.target === e.currentTarget) onClose()
      }}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        className={cn(
          'w-full rounded-lg border border-white/10 bg-pharmacist-bg shadow-xl outline-none',
          SIZES[size],
        )}
      >
        {(title || description) && (
          <header className="border-b border-white/10 px-5 py-4">
            {title ? (
              <h2 id="modal-title" className="text-base font-semibold text-white">
                {title}
              </h2>
            ) : null}
            {description ? <p className="mt-1 text-xs text-neutral-400">{description}</p> : null}
          </header>
        )}
        <div className="px-5 py-4">{children}</div>
        {footer ? (
          <footer className="flex items-center justify-end gap-2 border-t border-white/10 px-5 py-3">
            {footer}
          </footer>
        ) : null}
      </div>
    </div>
  )
}
